"use client";

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { TABLE_CONTROL_CLASS } from "./input";
import { Combobox as ComboboxPrimitive } from "@base-ui/react/combobox";
import { cva, type VariantProps } from "../../lib/cva";
import * as React from "react";
import { cn } from "../../lib/utils";
import { Icon } from "./icon";
import type { Omittable } from "../../lib/props";

// NOTE: `aria-label` belongs on SelectTrigger, not here — Root renders no
// element of its own, so a label passed to it never reaches the combobox.
export const Select: typeof SelectPrimitive.Root = SelectPrimitive.Root;

/**
 * Whether the enclosing root is a `SelectCombobox` (the filterable Select in
 * `select-combobox.tsx`) rather than a plain `Select`. `SelectItem` reads it
 * to render the matching primitive part, so ONE options renderer serves both
 * hosts. Internal wiring — only `SelectCombobox` provides it.
 */
export const SelectComboboxHostContext: React.Context<boolean> =
  React.createContext(false);

export const selectTriggerVariants = cva(
  "relative inline-flex min-h-8 select-none items-center justify-between gap-2 rounded-lg border border-input bg-background not-dark:bg-clip-padding px-[calc(--spacing(3)-1px)] text-left text-sm text-foreground outline-none ring-ring/24 transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:-translate-x-1/2 pointer-coarse:after:-translate-y-1/2 pointer-coarse:after:size-full pointer-coarse:after:min-h-11 focus-visible:border-ring focus-visible:ring-[3px] aria-invalid:border-destructive/36 focus-visible:aria-invalid:border-destructive/64 focus-visible:aria-invalid:ring-destructive/16 data-disabled:pointer-events-none data-disabled:opacity-64 dark:focus-visible:aria-invalid:ring-destructive/24 [&_svg:not([class*='opacity-'])]:opacity-80 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&>[data-slot=theme-swatch]]:-ms-1.5",
  {
    defaultVariants: {
      bleedStart: false,
      size: "default",
      variant: "default",
      width: "full",
    },
    compoundVariants: [
      // The offset is the trigger's own inset (padding + hairline), so it
      // rides the size axis: pull back exactly that far and the value lands
      // on the host's content edge.
      { bleedStart: true, size: ["default", "lg"], className: "-ms-3" },
      { bleedStart: true, size: "sm", className: "-ms-2.5" },
    ],
    variants: {
      // Chrome treatment. `outline`: page fill in both modes (no dark tint),
      // no shadow — the border is the control, brightening on hover; the
      // themed border stays reserved for focus. For form fields sitting on a
      // `background`-filled surface.
      variant: {
        // Hover rides a gradient wash OVER the opaque fill (the Button
        // `outline` mechanic): a bare `hover:bg-wash-hover` would swap the
        // fill for an alpha wash compositing against whatever sits behind
        // the trigger, flipping rest-darker controls hover-brighter on
        // lighter dark surfaces.
        default:
          "shadow-xs/5 [--select-wash:transparent] bg-linear-to-b from-(--select-wash) to-(--select-wash) hover:[--select-wash:var(--wash-hover)] data-popup-open:[--select-wash:var(--wash-active)] not-data-disabled:not-focus-visible:not-aria-invalid:not-data-pressed:before:shadow-[0_1px_--theme(--color-black/4%)] dark:bg-input/32 dark:not-data-disabled:not-focus-visible:not-aria-invalid:not-data-pressed:before:shadow-[0_-1px_--theme(--color-white/6%)] [[data-disabled],:focus-visible,[aria-invalid],[data-pressed]]:shadow-none",
        outline:
          "transition-[box-shadow,border-color] not-focus-visible:hover:border-muted-foreground/60",
        // `ghost`: no chrome at rest — the value and its chevron ARE the
        // control (a quiet inline select in a property row). State is the
        // bare alpha wash: with no opaque fill of its own to protect, it
        // composites correctly over whatever surface it sits on (the Button
        // `ghost` mechanic).
        ghost:
          "border-transparent bg-transparent hover:bg-wash-hover data-popup-open:bg-wash-active",
        // `table`: the cell is the control's edge — see `TABLE_CONTROL_CLASS`.
        table: TABLE_CONTROL_CLASS,
      },
      // A select in a form field spans its field; a select sitting in a row of
      // controls sizes to its own content. Baking `w-full` into the base made
      // every trigger a full-width block, so two in one flex row each asked for
      // 100% and wrapped onto separate lines instead of sitting side by side.
      // The 144px floor rides with `full` only — on `auto` it would defeat the
      // hug and pad every short value out to the same block.
      width: {
        auto: "w-auto",
        full: "w-full min-w-36",
      },
      size: {
        default: "",
        lg: "min-h-9",
        sm: "min-h-7 gap-1.5 px-[calc(--spacing(2.5)-1px)]",
      },
      // A quiet trigger sitting in column with plain values (pair with
      // `variant="ghost"`): a negative start margin cancels the trigger's own
      // inset, so the VALUE aligns with the flush text around it and the
      // hover wash overhangs into the gutter instead of indenting the value.
      // The per-size offsets live in `compoundVariants` above.
      bleedStart: {
        false: "",
        true: "",
      },
    },
  },
);

// Faint ink at full opacity (the explicit opacity class also opts the glyph
// out of the base rule's blanket opacity-80) — the affordance must read
// clearly quieter than the value it trails; dark muted-foreground sat too
// close to the value ink to separate on a real monitor.
export const selectTriggerIconClassName =
  "-me-1 size-4 text-faint-foreground opacity-100";

export type SelectTriggerProps = SelectPrimitive.Trigger.Props &
  VariantProps<typeof selectTriggerVariants> & Omittable<{
    /**
     * Whether to append the chevron affordance. Pass `false` when `render`
     * supplies a trigger that carries its own affordance (a filter chip, a
     * toggle) — the appended icon would otherwise land inside that element.
     * Passing `false` also drops the default trigger chrome, so the rendered
     * element keeps its own styling.
     */
    chevron?: boolean;
    /**
     * The affordance glyph. `chevrons-up-down` is the boxed form field's
     * swap-among-options mark; `chevron-down` is the quiet single-chevron a
     * chromeless trigger wears (pair it with `variant="ghost"`), where the
     * stacked carets would out-weigh the value they trail.
     */
    chevronIcon?: "chevrons-up-down" | "chevron-down";
  }>;

export function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  width = "full",
  bleedStart = false,
  chevron = true,
  chevronIcon = "chevrons-up-down",
  children,
  ...props
}: SelectTriggerProps): React.ReactElement {
  if (!chevron) {
    return (
      <SelectPrimitive.Trigger
        className={className}
        data-slot="select-trigger"
        {...props}
      >
        {children}
      </SelectPrimitive.Trigger>
    );
  }
  return (
    <SelectPrimitive.Trigger
      className={cn(
        selectTriggerVariants({ size, variant, width, bleedStart }),
        className,
      )}
      data-slot="select-trigger"
      {...props}
    >
      {children}
      <SelectPrimitive.Icon data-slot="select-icon">
        <Icon className={selectTriggerIconClassName} name={chevronIcon} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

export function SelectValue({
  className,
  ...props
}: SelectPrimitive.Value.Props): React.ReactElement {
  return (
    <SelectPrimitive.Value
      className={cn(
        "flex-1 truncate data-placeholder:text-muted-foreground",
        className,
      )}
      data-slot="select-value"
      {...props}
    />
  );
}

export function SelectPopup({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  align = "start",
  alignOffset = 0,
  alignItemWithTrigger = true,
  anchor,
  portalProps,
  "aria-label": ariaLabel,
  ...props
}: SelectPrimitive.Popup.Props & Omittable<{
  portalProps?: SelectPrimitive.Portal.Props;
  side?: SelectPrimitive.Positioner.Props["side"];
  sideOffset?: SelectPrimitive.Positioner.Props["sideOffset"];
  align?: SelectPrimitive.Positioner.Props["align"];
  alignOffset?: SelectPrimitive.Positioner.Props["alignOffset"];
  alignItemWithTrigger?: SelectPrimitive.Positioner.Props["alignItemWithTrigger"];
  anchor?: SelectPrimitive.Positioner.Props["anchor"];
}>): React.ReactElement {
  return (
    <SelectPrimitive.Portal {...portalProps}>
      <SelectPrimitive.Positioner
        align={align}
        alignItemWithTrigger={alignItemWithTrigger}
        alignOffset={alignOffset}
        anchor={anchor}
        className="z-50 select-none"
        data-slot="select-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <SelectPrimitive.Popup
          className="origin-(--transform-origin) text-foreground outline-none"
          // Portalled, so it inherits no surface scope — names its own, and
          // descendant `border-border` draws the popover-tuned hairline.
          data-on="popover"
          data-slot="select-popup"
          {...props}
        >
          <SelectPrimitive.ScrollUpArrow
            className="top-0 z-50 flex h-6 w-full cursor-default items-center justify-center before:pointer-events-none before:absolute before:inset-x-px before:top-px before:h-[200%] before:rounded-t-[calc(var(--radius-lg)-1px)] before:bg-linear-to-b before:from-50% before:from-popover"
            data-slot="select-scroll-up-arrow"
          >
            <Icon className="relative size-4" name="chevron-up" />
          </SelectPrimitive.ScrollUpArrow>
          <div className="relative h-full min-w-(--anchor-width) rounded-lg border bg-popover not-dark:bg-clip-padding shadow-popover before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-lg)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]">
            {/* The list is the listbox, so the popup's name goes on it. */}
            <SelectPrimitive.List
              aria-label={ariaLabel}
              className={cn(
                "max-h-(--available-height) overflow-y-auto p-1",
                className,
              )}
              data-slot="select-list"
            >
              {children}
            </SelectPrimitive.List>
          </div>
          <SelectPrimitive.ScrollDownArrow
            className="bottom-0 z-50 flex h-6 w-full cursor-default items-center justify-center before:pointer-events-none before:absolute before:inset-x-px before:bottom-px before:h-[200%] before:rounded-b-[calc(var(--radius-lg)-1px)] before:bg-linear-to-t before:from-50% before:from-popover"
            data-slot="select-scroll-down-arrow"
          >
            <Icon className="relative size-4" name="chevron-down" />
          </SelectPrimitive.ScrollDownArrow>
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

export function SelectItem({
  className,
  children,
  description,
  indicator = "start",
  size = "default",
  ...props
}: SelectPrimitive.Item.Props &
  Omittable<{
    /** Which side the selected checkmark sits on. `end` (the reference
     * trailing check) suits items whose leading cell is already occupied —
     * a swatch, an avatar — so the label column stays flush-left. */
    indicator?: "start" | "end";
    /** Row height. `lg` (32px desktop, the control rung) suits items
     * carrying a visual — a swatch, an avatar — that crowds the 28px row. */
    size?: "default" | "lg";
    /** Quiet second line under the label, for an option whose label alone
     * doesn't say what picking it means. It sits outside the item text, so
     * keyboard typeahead still matches on the label only. */
    description?: React.ReactNode;
  }>): React.ReactElement {
  const comboboxHost = React.useContext(SelectComboboxHostContext);
  const descriptionId = React.useId();
  const trailing = indicator === "end";
  const rowClassName = cn(
    "grid in-data-[side=none]:min-w-[calc(var(--anchor-width)+1.25rem)] cursor-pointer items-center gap-2 rounded-sm py-1 text-sm outline-none data-disabled:pointer-events-none data-highlighted:bg-wash-hover data-highlighted:text-foreground data-disabled:opacity-64 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
    size === "lg" ? "min-h-8" : "min-h-7",
    trailing
      ? "grid-cols-[1fr_1rem] ps-3 pe-2"
      : "grid-cols-[1rem_1fr] ps-2 pe-4",
    // The grid's 8px gap is the label↔indicator column gap; between the label
    // line and its description it reads as a paragraph break, so the row gap
    // tightens to 2px when a second line exists.
    description != null && "gap-y-0.5",
    className,
  );
  const indicatorClassName = trailing
    ? "col-start-2 row-start-1"
    : "col-start-1";
  const check = (
    <svg
      aria-hidden="true"
      fill="none"
      height="24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5.252 12.7 10.2 18.63 18.748 5.37" />
    </svg>
  );
  // Flex row (on the text cell below) so an icon passed beside the label sits
  // inline with it — preflight's `svg { display: block }` would otherwise
  // stack a block-level icon above the text inside this span.
  const textClassName = cn(
    "flex min-w-0 items-center gap-2",
    trailing ? "col-start-1 row-start-1" : "col-start-2",
  );
  // The span is aria-hidden so the option's accessible NAME stays the label
  // alone; `aria-describedby` still computes text from a hidden element it
  // references directly, so assistive tech reads it as the description.
  const descriptionNode =
    description != null ? (
      <span
        aria-hidden
        className={cn(
          "row-start-2 min-w-0 text-muted-foreground text-xs",
          trailing ? "col-start-1" : "col-start-2",
        )}
        id={descriptionId}
      >
        {description}
      </span>
    ) : null;
  const describedBy = description != null ? descriptionId : undefined;
  if (comboboxHost) {
    // Same row, rendered as the combobox's item part. Only the selection
    // surface travels — a shared options renderer passes `value`/`disabled`
    // and nothing else; the plain-Select part surface stays on that branch.
    return (
      <ComboboxPrimitive.Item
        aria-describedby={describedBy}
        className={rowClassName}
        data-slot="select-item"
        disabled={props.disabled}
        value={props.value}
      >
        <ComboboxPrimitive.ItemIndicator className={indicatorClassName}>
          {check}
        </ComboboxPrimitive.ItemIndicator>
        <span className={textClassName}>{children}</span>
        {descriptionNode}
      </ComboboxPrimitive.Item>
    );
  }
  return (
    <SelectPrimitive.Item
      aria-describedby={describedBy}
      className={rowClassName}
      data-slot="select-item"
      {...props}
    >
      <SelectPrimitive.ItemIndicator className={indicatorClassName}>
        {check}
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText className={textClassName}>
        {children}
      </SelectPrimitive.ItemText>
      {descriptionNode}
    </SelectPrimitive.Item>
  );
}

export function SelectSeparator({
  className,
  ...props
}: SelectPrimitive.Separator.Props): React.ReactElement {
  return (
    <SelectPrimitive.Separator
      className={cn("mx-2 my-1 h-px bg-border", className)}
      data-slot="select-separator"
      {...props}
    />
  );
}

/** A group of options. Renders as the host's group part — a `SelectCombobox`
 * hosts the same `SelectGroup`/`SelectGroupLabel` children a plain `Select`
 * does, so one grouped options renderer serves both. */
export function SelectGroup(
  props: SelectPrimitive.Group.Props,
): React.ReactElement {
  const comboboxHost = React.useContext(SelectComboboxHostContext);
  if (comboboxHost) {
    return (
      <ComboboxPrimitive.Group
        className={props.className}
        data-slot="select-group"
      >
        {props.children}
      </ComboboxPrimitive.Group>
    );
  }
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

export function SelectLabel({
  className,
  ...props
}: SelectPrimitive.Label.Props): React.ReactElement {
  return (
    <SelectPrimitive.Label
      className={cn(
        "not-in-data-[slot=field]:mb-2 inline-flex cursor-default items-center gap-2 font-medium text-sm/4 text-foreground",
        className,
      )}
      data-slot="select-label"
      {...props}
    />
  );
}

const selectGroupLabelClassName =
  "px-2 py-1.5 font-medium text-muted-foreground text-xs";

export function SelectGroupLabel(
  props: SelectPrimitive.GroupLabel.Props,
): React.ReactElement {
  const comboboxHost = React.useContext(SelectComboboxHostContext);
  if (comboboxHost) {
    return (
      <ComboboxPrimitive.GroupLabel
        className={selectGroupLabelClassName}
        data-slot="select-group-label"
      >
        {props.children}
      </ComboboxPrimitive.GroupLabel>
    );
  }
  return (
    <SelectPrimitive.GroupLabel
      className={selectGroupLabelClassName}
      data-slot="select-group-label"
      {...props}
    />
  );
}

/** One row of an {@link OptionSelect}: the stored value and what it reads as. */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type OptionSelectProps = Omittable<{
  options: readonly SelectOption[];
  value: string;
  onValueChange: (value: string) => void;
  /** Names the combobox. It rides the trigger, which is the labelled element. */
  "aria-label": string;
  /** Trigger width. `auto` sizes to the value, which suits a settings row. */
  width?: "auto" | "full";
  /** Which side the selected checkmark sits on. */
  indicator?: "start" | "end";
  disabled?: boolean;
}>;

/**
 * A select whose popup is a LIST OF OPTIONS — the shape most selects are.
 *
 * The compound parts underneath (`Select` · `SelectTrigger` · `SelectValue` ·
 * `SelectPopup` · `SelectItem`) exist for popups that carry more than a label:
 * a swatch, an avatar, a group heading, a separator. When every row is just
 * text, that anatomy is five nested elements and a `.map` restated per call
 * site, and the option list ends up written twice — once for `items`, which is
 * what lets the trigger render the CURRENT value's label, and once for the
 * rows. Passing the options as data writes both from one source.
 *
 * It also narrows the change handler. The primitive reports `unknown`, because
 * a value can be any type it was given; an options list is strings, so callers
 * get a `string` rather than repeating the same type guard.
 *
 * Reach for the parts when a row needs more than a label — this is the façade,
 * not a replacement.
 */
export function OptionSelect({
  options,
  value,
  onValueChange,
  "aria-label": ariaLabel,
  width = "auto",
  indicator = "end",
  disabled,
}: OptionSelectProps): React.ReactElement {
  return (
    <Select
      // `items` is what the trigger reads the current value's LABEL from, so
      // it carries the same list the rows do. Copied because the prop is
      // mutable and an options list is declared `readonly`.
      items={options.map((option) => ({ ...option }))}
      {...(disabled === undefined ? {} : { disabled })}
      onValueChange={(next) => {
        if (typeof next === "string") onValueChange(next);
      }}
      value={value}
    >
      <SelectTrigger aria-label={ariaLabel} width={width}>
        <SelectValue />
      </SelectTrigger>
      <SelectPopup>
        {options.map((option) => (
          <SelectItem
            {...(option.disabled === undefined
              ? {}
              : { disabled: option.disabled })}
            indicator={indicator}
            key={option.value}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectPopup>
    </Select>
  );
}
